/**
 * Affiliate Region Configuration
 *
 * Central registry mapping user country to affiliate network settings,
 * Amazon domains, Google Search geo parameters, and currency.
 *
 * Phase 1 + Phase 2 of geo-aware affiliate routing (Issue #191 / #210).
 * Phase 1: region registry + manual country selector.
 * Phase 2: auto-detect via timezone + browser language.
 *
 * @module config/affiliateConfig
 */

// =============================================================================
// Types
// =============================================================================

/** Regions where affiliate routing is supported */
export type SupportedRegion = 'US' | 'KR' | 'BR' | 'MX' | 'SG' | 'CA' | 'UK' | 'DE' | 'FR' | 'IT' | 'ES' | 'NL' | 'SE' | 'PL';

/** Affiliate network identifiers (superset — includes future networks) */
export type AffiliateNetworkId = 'amazon' | 'coupang' | 'iherb' | 'yesstyle';

export interface RegionConfig {
  /** ISO 3166-1 alpha-2 */
  country: SupportedRegion;
  /** English display label */
  label: string;
  /** Korean display label */
  labelKo: string;
  /** Flag emoji */
  flag: string;
  /** ISO 4217 currency code */
  currency: string;
  /** Currency symbol for display */
  currencySymbol: string;
  /** Google Custom Search `gl` param (lowercase) */
  gl: string;
  /** Default Google Custom Search `hl` param */
  hl: string;
  /** Amazon marketplace config for this region (null if Amazon not available) */
  amazon: {
    /** e.g. 'amazon.com', 'amazon.com.br' */
    domain: string;
    /** Vite env var name that holds the affiliate tag for this region */
    tagEnvVar: string;
  } | null;
  /** Primary affiliate network for product search */
  primaryNetwork: AffiliateNetworkId;
  /** Ordered list of networks available in this region */
  availableNetworks: AffiliateNetworkId[];
}

// =============================================================================
// Region Registry
// =============================================================================

export const REGION_CONFIG: Record<SupportedRegion, RegionConfig> = {
  US: {
    country: 'US',
    label: 'United States',
    labelKo: '미국',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    gl: 'us',
    hl: 'en',
    amazon: {
      domain: 'amazon.com',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG',
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  KR: {
    country: 'KR',
    label: 'South Korea',
    labelKo: '대한민국',
    flag: '🇰🇷',
    currency: 'KRW',
    currencySymbol: '₩',
    gl: 'kr',
    hl: 'ko',
    amazon: null, // Coupang is primary in Korea
    primaryNetwork: 'coupang',
    availableNetworks: ['coupang', 'iherb', 'yesstyle'],
  },
  BR: {
    country: 'BR',
    label: 'Brazil',
    labelKo: '브라질',
    flag: '🇧🇷',
    currency: 'BRL',
    currencySymbol: 'R$',
    gl: 'br',
    hl: 'pt-BR',
    amazon: {
      domain: 'amazon.com.br',
      tagEnvVar: 'VITE_AMAZON_TAG_BR',
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  MX: {
    country: 'MX',
    label: 'Mexico',
    labelKo: '멕시코',
    flag: '🇲🇽',
    currency: 'MXN',
    currencySymbol: '$',
    gl: 'mx',
    hl: 'es',
    amazon: {
      domain: 'amazon.com.mx',
      tagEnvVar: 'VITE_AMAZON_TAG_MX',
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  SG: {
    country: 'SG',
    label: 'Singapore',
    labelKo: '싱가포르',
    flag: '🇸🇬',
    currency: 'SGD',
    currencySymbol: 'S$',
    gl: 'sg',
    hl: 'en',
    amazon: {
      domain: 'amazon.sg',
      tagEnvVar: 'VITE_AMAZON_TAG_SG',
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  CA: {
    country: 'CA',
    label: 'Canada',
    labelKo: '캐나다',
    flag: '🇨🇦',
    currency: 'CAD',
    currencySymbol: 'C$',
    gl: 'ca',
    hl: 'en',
    amazon: {
      domain: 'amazon.ca',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  UK: {
    country: 'UK',
    label: 'United Kingdom',
    labelKo: '영국',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    gl: 'uk',
    hl: 'en',
    amazon: {
      domain: 'amazon.co.uk',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  DE: {
    country: 'DE',
    label: 'Germany',
    labelKo: '독일',
    flag: '🇩🇪',
    currency: 'EUR',
    currencySymbol: '€',
    gl: 'de',
    hl: 'de',
    amazon: {
      domain: 'amazon.de',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  FR: {
    country: 'FR',
    label: 'France',
    labelKo: '프랑스',
    flag: '🇫🇷',
    currency: 'EUR',
    currencySymbol: '€',
    gl: 'fr',
    hl: 'fr',
    amazon: {
      domain: 'amazon.fr',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  IT: {
    country: 'IT',
    label: 'Italy',
    labelKo: '이탈리아',
    flag: '🇮🇹',
    currency: 'EUR',
    currencySymbol: '€',
    gl: 'it',
    hl: 'it',
    amazon: {
      domain: 'amazon.it',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  ES: {
    country: 'ES',
    label: 'Spain',
    labelKo: '스페인',
    flag: '🇪🇸',
    currency: 'EUR',
    currencySymbol: '€',
    gl: 'es',
    hl: 'es',
    amazon: {
      domain: 'amazon.es',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  NL: {
    country: 'NL',
    label: 'Netherlands',
    labelKo: '네덜란드',
    flag: '🇳🇱',
    currency: 'EUR',
    currencySymbol: '€',
    gl: 'nl',
    hl: 'nl',
    amazon: {
      domain: 'amazon.nl',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  SE: {
    country: 'SE',
    label: 'Sweden',
    labelKo: '스웨덴',
    flag: '🇸🇪',
    currency: 'SEK',
    currencySymbol: 'kr',
    gl: 'se',
    hl: 'sv',
    amazon: {
      domain: 'amazon.se',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
  PL: {
    country: 'PL',
    label: 'Poland',
    labelKo: '폴란드',
    flag: '🇵🇱',
    currency: 'PLN',
    currencySymbol: 'zł',
    gl: 'pl',
    hl: 'pl',
    amazon: {
      domain: 'amazon.pl',
      tagEnvVar: 'VITE_AMAZON_AFFILIATE_TAG', // OneLink auto-redirect from US tag
    },
    primaryNetwork: 'amazon',
    availableNetworks: ['amazon', 'iherb', 'yesstyle'],
  },
};

// =============================================================================
// Helpers
// =============================================================================

/** All supported regions as array (for dropdowns, iteration) */
export const SUPPORTED_REGIONS: SupportedRegion[] = [
  'US', 'KR', 'BR', 'MX', 'SG',
  'CA', 'UK', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'PL',
];

/** Map of currency code → region (for reverse lookup) */
export const CURRENCY_TO_REGION: Record<string, SupportedRegion> = {
  USD: 'US',
  KRW: 'KR',
  BRL: 'BR',
  MXN: 'MX',
  SGD: 'SG',
  CAD: 'CA',
  GBP: 'UK',
  SEK: 'SE',
  PLN: 'PL',
  // EUR maps to DE by default; actual region is refined via timezone/language
  EUR: 'DE',
};

/**
 * Get region config for a country code, defaulting to US if unknown.
 */
export function getRegionConfig(country: string | undefined): RegionConfig {
  const upper = (country ?? 'US').trim().toUpperCase() as SupportedRegion;
  return REGION_CONFIG[upper] || REGION_CONFIG.US;
}

/**
 * Get the Amazon affiliate tag for a given region from env vars.
 * Returns empty string if not configured (tag enrollment pending).
 * Falls back to US tag for regions without a dedicated Amazon marketplace (e.g. KR).
 */
export function getAmazonTagForRegion(region: SupportedRegion): string {
  const config = REGION_CONFIG[region];
  if (!config?.amazon) {
    // Region has no Amazon marketplace — fall back to US tag so global Amazon links keep tagging
    return import.meta.env.VITE_AMAZON_AFFILIATE_TAG || '';
  }

  // Dynamic lookup from Vite env vars
  const envVars: Record<string, string> = {
    VITE_AMAZON_AFFILIATE_TAG: import.meta.env.VITE_AMAZON_AFFILIATE_TAG || '',
    VITE_AMAZON_TAG_BR: import.meta.env.VITE_AMAZON_TAG_BR || '',
    VITE_AMAZON_TAG_MX: import.meta.env.VITE_AMAZON_TAG_MX || '',
    VITE_AMAZON_TAG_SG: import.meta.env.VITE_AMAZON_TAG_SG || '',
  };

  return envVars[config.amazon.tagEnvVar] || '';
}

/**
 * Get the voice/AI language code for a user's region.
 * Uses the region's `hl` field (e.g. DE→'de', FR→'fr', SE→'sv').
 * This is separate from the UI language (only en/ko/pt-BR/es) — voice mode
 * should speak the region's native language even if the UI hasn't been translated.
 */
export function getVoiceLanguageForRegion(country: string | undefined): string {
  return getRegionConfig(country).hl;
}

/**
 * Resolve the `gl` and `hl` parameters for Google Search based on region + language.
 *
 * `gl` is always the region's country code — it boosts results from the user's
 * country regardless of language (shipping, pricing relevance).
 *
 * `hl` is the user's actual UI language — so a Brazilian user with an English
 * UI gets English-language results from their regional Amazon marketplace.
 */
export function getSearchParams(region: SupportedRegion, uiLanguage: string): { gl: string; hl: string } {
  const config = REGION_CONFIG[region];
  return {
    gl: config.gl,
    hl: uiLanguage.split('-')[0],
  };
}

// =============================================================================
// Timezone → Region mapping
// =============================================================================

/** Map IANA timezone prefixes/names to supported regions */
const TIMEZONE_TO_REGION: Record<string, SupportedRegion> = {
  // Brazil
  'America/Sao_Paulo': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Bahia': 'BR',
  'America/Belem': 'BR',
  'America/Manaus': 'BR',
  'America/Cuiaba': 'BR',
  'America/Porto_Velho': 'BR',
  'America/Campo_Grande': 'BR',
  'America/Rio_Branco': 'BR',
  'America/Araguaina': 'BR',
  'America/Maceio': 'BR',
  'America/Noronha': 'BR',
  // Mexico
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Monterrey': 'MX',
  'America/Merida': 'MX',
  'America/Chihuahua': 'MX',
  'America/Hermosillo': 'MX',
  'America/Mazatlan': 'MX',
  'America/Tijuana': 'MX',
  'America/Matamoros': 'MX',
  'America/Ojinaga': 'MX',
  'America/Bahia_Banderas': 'MX',
  // Singapore
  'Asia/Singapore': 'SG',
  // South Korea
  'Asia/Seoul': 'KR',
  // Canada
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/Regina': 'CA',
  // United Kingdom
  'Europe/London': 'UK',
  // Germany
  'Europe/Berlin': 'DE',
  // France
  'Europe/Paris': 'FR',
  // Italy
  'Europe/Rome': 'IT',
  // Spain
  'Europe/Madrid': 'ES',
  // Netherlands
  'Europe/Amsterdam': 'NL',
  // Sweden
  'Europe/Stockholm': 'SE',
  // Poland
  'Europe/Warsaw': 'PL',
};

/**
 * Attempt to detect region from the user's timezone.
 * Returns undefined if the timezone doesn't map to a supported region.
 */
function detectRegionFromTimezone(): SupportedRegion | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_TO_REGION[tz];
  } catch {
    return undefined;
  }
}

/**
 * Detect the user's region using a 3-layer cascade:
 *   1. Saved profile preference (explicit user choice)
 *   2. Timezone heuristic (Intl API)
 *   3. Browser language fallback (navigator.language)
 *
 * Language fallback uses exact locale tags (e.g. `es-mx`) rather than bare
 * prefixes so that `es-ES` (Spain) or `es-US` (US Hispanic) users are not
 * misclassified as Mexico.
 *
 * Defaults to 'US' if none match.
 */
export function detectUserRegion(profileCountry: string | undefined): SupportedRegion {
  // Layer 1: Saved profile preference
  if (profileCountry) {
    const upper = profileCountry.toUpperCase() as SupportedRegion;
    if (REGION_CONFIG[upper]) return upper;
  }

  // Layer 2: Timezone heuristic
  const tzRegion = detectRegionFromTimezone();
  if (tzRegion) return tzRegion;

  // Layer 3: Browser language fallback — guard for non-browser contexts (SSR / tests)
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '').toLowerCase();
  if (lang.startsWith('ko')) return 'KR';
  if (lang === 'pt-br' || lang.startsWith('pt')) return 'BR';
  if (lang === 'es-mx') return 'MX';
  if (lang === 'es-es') return 'ES';
  if (lang.startsWith('de')) return 'DE';
  if (lang.startsWith('fr-ca') || lang === 'fr-ca') return 'CA';
  if (lang.startsWith('fr')) return 'FR';
  if (lang.startsWith('it')) return 'IT';
  if (lang.startsWith('nl')) return 'NL';
  if (lang.startsWith('sv')) return 'SE';
  if (lang.startsWith('pl')) return 'PL';
  if (lang.startsWith('en-gb')) return 'UK';
  if (lang.startsWith('en-ca')) return 'CA';

  return 'US';
}
